import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import {
	withStyles,
	Theme,
	createStyles,
	lighten,
	makeStyles,
} from '@material-ui/core/styles';
import {
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	LinearProgress,
	IconButton,
	Tooltip,
	CircularProgress,
} from "@material-ui/core";

import { getAllData, getLocation } from '../../apis/db.service';

import MapDirectionsIcon from './assets/directionIcon.png';
import MapAllLocatinsIcon from './assets/map-locations.png';
import bg from './assets/pic.png';

const useHomeStyles = makeStyles(() =>
	createStyles({
		root: {
			flexGrow: 1,
			background: 'light-gray'
		},
		Grid__root: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
		},
		AppBar__title: {
			flexGrow: 1,
		},
		table__root: {
			display: 'block',
		},
		container: {
			maxHeight: 440,
			boxSizing: 'border-box',
		},
		progressBar: {
			width: '100px',
		},
		progressLabel: {
			position: "absolute",
			fontSize: '12px',
			width: '100px',
			fontWeight: 'bold',
			zIndex: 1,
			maxHeight: "15px",
			textAlign: "center",
			display: "flex",
			alignItems: "center",
			"& span": {
				width: "100%"
			}
		},
		Icon: {
			borderRadius: '0',
			padding: '5px',
		},
		Grid: {
			display: 'flex',
			flexDirection: 'column',
			padding: '0',
			margin: '8px',
		},
	}),
);


const BorderLinearProgressGreen = withStyles({
	root: {
		height: 15,
		backgroundColor: lighten("rgb(202,193,193)", 0.5),
		borderRadius: 100,
	},
	bar: {
		borderRadius: 100,
		backgroundColor: "#4d9b35"
	},
})(LinearProgress);

const BorderLinearProgressYellow = withStyles({
	root: {
		height: 15,
		backgroundColor: lighten("rgb(202,193,193)", 0.5),
		borderRadius: 100,
	},
	bar: {
		borderRadius: 100,
		backgroundColor: "#ffcc00"
	},
})(LinearProgress);

const BorderLinearProgressRed = withStyles({
	root: {
		height: 15,
		backgroundColor: lighten("rgb(202,193,193)", 0.5),
		borderRadius: 100,
	},
	bar: {
		borderRadius: 100,
		backgroundColor: "#F32013"
	},
})(LinearProgress);

const StyledTableCell = withStyles((theme: Theme) =>
	createStyles({
		head: {
			backgroundColor: theme.palette.common.black,
			color: theme.palette.common.white,
		},
		body: {
			fontSize: 14,
		},
	}),
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
	createStyles({
		root: {
			'&:nth-of-type(odd)': {
				backgroundColor: '#eee',
			},
			'&:nth-of-type(even)': {
				backgroundColor: '#fff',
			},
		},
	}),
)(TableRow);

interface Column {
	id:
	| 'id'
	| 'filled'
	| 'distance'
	| 'time'
	| 'modify_date'
	| 'modify_time'
	| 'actions';
	label: string;
	minWidth?: number;
	align?: 'center';
	format?: (value: number) => string;
}

const columns: Column[] = [
	{
		id: 'id',
		label: 'Node ID',
		minWidth: 50,
	},
	{
		id: 'filled',
		label: 'Filled',
		minWidth: 100,
	},
	{
		id: 'distance',
		label: 'Distance',
		minWidth: 50,
	},
	{
		id: 'time',
		label: 'Estimated Time',
		minWidth: 50,
	},
	{
		id: 'modify_date',
		label: 'Modified In',
		minWidth: 50
	},
	{
		id: 'modify_time',
		label: 'Modified At',
		minWidth: 50
	},
	{
		id: 'actions',
		label: 'Actions',
		minWidth: 50
	},
];

interface Data {
	id: string;
	filled: string;
	distance: string;
	time: string;
	modify_date: string;
	modify_time: string;
}

const createData = (
	id: string,
	filled: string,
	distance: string,
	time: string,
	modify_date: string,
	modify_time: string
): Data => {
	return {
		id,
		filled,
		distance,
		time,
		modify_date,
		modify_time
	};
};

const Home = () => {
	const classes = useHomeStyles();
	const [isLoading, setIsLoading] = React.useState(true);

	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(5);
	const [allData, setAllData] = React.useState([]);
	const [tableData, setTableData] = React.useState([]);

	const [latitude2, setLatitude] = React.useState('');
	const [longitude2, setLongitude] = React.useState('');
	let latitude = '';
	let longitude = '';

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const createRowsData = (dataFromAPI: any) => {
		const temp = dataFromAPI.map((data: any) => {
			return (createData(
				data.id,
				data.filled,
				data.distance,
				data.time,
				data.modify_date,
				data.modify_time,
			));
		});

		// Sort the data first upon percentage, then upon distance
		temp.sort(function (a, b) {
			if (parseFloat(a.filled) === parseFloat(b.filled)) {
				return (parseFloat((a.distance).split(" ")[0]) >
					parseFloat((b.distance).split(" ")[0]) ? 1 : -1);
			}
			else {
				return (parseFloat(a.filled) > parseFloat(b.filled) ? -1 : 1);
			}
		});

		setTableData(temp);
		setIsLoading(false);
	};

	const getPosition = async () => {
		return new Promise(function (resolve, reject) {
			window.navigator && window.navigator.geolocation.getCurrentPosition(resolve, reject);
		});
	};

	const showPath = (index: any) => {
		// @ts-ignore
		let data = allData.find(e => e.id === tableData[index].id);
		// @ts-ignore
		if (data.distance !== '-') {
			// @ts-ignore
			window.open('https://maps.google.com/maps?saddr=' + latitude2 + ',' + longitude2 + '&daddr=' + data.latitude + ',' + data.longitude);
		}
	};

	// Similar to componentDidMount and componentDidUpdate:
	React.useEffect(() => {
		setInterval(() => {
			getPosition()
				.then((position: any) => {
					latitude = String(position.coords.latitude);
					longitude = String(position.coords.longitude);
					setLatitude(latitude);
					setLongitude(longitude);

					getAllData(latitude, longitude)
						.then(allData => {
							setAllData(allData.data);
							createRowsData(allData.data);
						})
						.catch(err => {
							console.log(err, "Error in fetching data")
						});
				}).catch(err => {
					// Secure Origins are e.g [localhost, https]
					// Unsecure Origins are e.g [192.168.1.10, http]
					if (err.message && err.message.startsWith("Only secure origins are allowed")) {
						getLocation()
							.then(resp => {
								latitude = resp.data.lat;
								longitude = resp.data.lon;
								setLatitude(latitude);
								setLongitude(longitude);
								getAllData(latitude, longitude)
									.then(allData => {
										setAllData(allData.data);
										createRowsData(allData.data);
									})
									.catch(err => {
										console.log(err, "Error in fetching data")
									});
							})
					} else {
						console.log(err, "Error in fetching current location")
					}
				});
		}, 10000)
	}, []);

	return (
		isLoading ?
			(
				<div
					className={classes.root}
					style={{
						display: 'flex',
						justifyContent: 'center',
					}}>
					<CircularProgress
						style={{
							color: 'gray',
						}} />
				</div>
			)
			: (
				<div className={classes.root}>
					<div
						className={classes.Grid__root}
						style={{
							marginTop: '120px'
						}}
					>
						<img src={bg} alt="website logo" />
					</div>
					<Grid
						spacing={2}
						className={classes.Grid__root}
					>
						<Grid
							item
							xs={12}
							className={classes.Grid}
						>
							{
								(allData.length !== 0) &&
								(
									<Link to={{
										pathname: "/allLocations",
										state: {
											places: allData,
										}
									}}
										className={classes.Icon}
										style={{
											alignSelf: 'flex-end',
											textDecoration: 'none',
										}}
									>
										<Tooltip
											title="show all locations"
											aria-label="show all locations">
											<img
												src={MapAllLocatinsIcon}
												alt='AllLocatins'
												style={{
													width: '24px',
													height: '24px',
												}} />
										</Tooltip>
									</Link>)
							}

							<Paper className={classes.table__root}>
								<TableContainer className={classes.container}>
									<Table stickyHeader aria-label="sticky table">
										<TableHead>
											<TableRow>
												{columns.map(column => (
													<StyledTableCell
														key={column.id}
														align={column.align}
														style={{
															minWidth: column.minWidth,
															fontWeight: 'bold'
														}}
													>
														{column.label}
													</StyledTableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{tableData
												.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
												.map((row, index) => {
													return (
														<StyledTableRow
															hover
															role="checkbox"
															tabIndex={-1}
														// key={row['id']}
														>
															{columns.map(column => {
																const value = row[column.id];
																if (column.id === 'filled') {
																	return (
																		<StyledTableCell key={column.id}
																			align={column.align}>
																			<div className={classes.progressLabel}>
																				<span>{value} %</span>
																			</div>
																			{
																				value >= 70 ?
																					<BorderLinearProgressRed
																						className={classes.progressBar}
																						variant="determinate"
																						value={value}
																					/>
																					:
																					value >= 40 ?
																						<BorderLinearProgressYellow
																							className={classes.progressBar}
																							variant="determinate"
																							value={value}
																						/>
																						:
																						<BorderLinearProgressGreen
																							className={classes.progressBar}
																							variant="determinate"
																							value={value}
																						/>
																			}
																		</StyledTableCell>
																	);
																} else if (column.id === 'actions') {
																	return (
																		<StyledTableCell
																			key={column.id}
																			align={column.align}>
																			<IconButton
																				onClick={() => showPath(index)}
																				className={classes.Icon}>
																				<Tooltip
																					title="show path"
																					aria-label="show path">
																					<img
																						src={MapDirectionsIcon}
																						alt='MapDirections'
																						style={{
																							width: '24px',
																							height: '24px',
																						}} />
																				</Tooltip>
																			</IconButton>
																		</StyledTableCell>
																	);
																} else {
																	return (
																		<StyledTableCell key={column.id}
																			align={column.align}>
																			{column.format && typeof value === 'number'
																				? column.format(value)
																				: value}
																		</StyledTableCell>
																	);
																}
															})}
														</StyledTableRow>
													);
												})}
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									rowsPerPageOptions={[5, 15, 25]}
									component="div"
									count={tableData.length}
									rowsPerPage={rowsPerPage}
									page={page}
									onChangePage={handleChangePage}
									onChangeRowsPerPage={handleChangeRowsPerPage}
								/>
							</Paper>
						</Grid>
					</Grid>
				</div>
			));
};

export default withRouter(Home);

